/* Common Imports */

import { styled } from "@mui/system";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";

/* Component Imports */

import { Typography } from "@mui/material";
import PropertyCardBroadcastPreviewFetcher from "../../cards/PropertyCardBroadcastPreview/PropertyCardBroadcastPreviewFetcher";

/* Icon Imports */

import { EditOutlined } from "@mui/icons-material";

/* Styled Components */

const Container = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	height: "100%",
	minHeight: "100%",
	flex: 1,
	position: "relative",
});

const HeaderContainer = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
	background: "#621B16",
	borderRadius: "0 0 1rem 1rem",
	color: "#fff",
	padding: "2rem 1.5rem 1.5rem 1.5rem",
});

const Title = styled(Typography)({
	fontWeight: 700,
	fontSize: "1.25rem",
	textAlign: "center",
	color: "#FFFFFF",
});

const SubTitle = styled(Typography)({
	color: "#FFFFFF",
	fontWeight: 400,
	fontSize: "0.875rem",
	textAlign: "center",
	padding: "0rem 0rem 0.5rem 0rem",
});

const Section = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "1.75rem",
	padding: "2rem 1.5rem 1rem 1.5rem",
});

const ValueSection = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
});

const ValueContainer = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
});

const Label = styled("div")({
	fontWeight: 400,
	fontSize: "0.825rem",
});

const Value = styled(Typography)({
	fontWeight: 700,
	fontSize: "0.875rem",
});

export default function Summary({
	broadcast,
	propertyId,
	setPage,
	router,
	dispatch,
	loading,
}: {
	broadcast: StateInterface["broadcast_data"] | undefined | null;
	propertyId: string | ObjectId;
	setPage: Function;
	router: NextRouter;
	dispatch: Function;
	loading: boolean;
}) {
	/* Translations */

	const localLang = {
		days: router.locale === "ar-AE" ? "يوم " : router.locale === "kn-IN" ? " ದಿನಗಳು" : " days",
		preview_your_broadcast_details:
			router.locale === "ar-AE"
				? "معاينة تفاصيل البث الخاص بك"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಪ್ರಸಾರ ವಿವರಗಳನ್ನು ಪೂರ್ವವೀಕ್ಷಿಸಿ"
					: "Preview your broadcast details",
		broadcasting_to:
			router.locale === "ar-AE"
				? "يتم البث إلى"
				: router.locale === "kn-IN"
					? "ಪ್ರಸಾರ ಮಾಡಲಾಗುತ್ತಿದೆ"
					: "Broadcasting to",
		agents: router.locale === "ar-AE" ? "وكلاء" : router.locale === "kn-IN" ? "ಏಜೆಂಟ್‌ಗಳು" : "agents",
		brokerage_offered:
			router.locale === "ar-AE"
				? "العمولة المقدمة"
				: router.locale === "kn-IN"
					? "ನೀಡಲಾದ ಬ್ರೋಕರೇಜ್"
					: "Brokerage offered",
		additional_note:
			router.locale === "ar-AE"
				? "ملاحظة إضافية"
				: router.locale === "kn-IN"
					? "ಹೆಚ್ಚುವರಿ ಟಿಪ್ಪಣಿ"
					: "Additional notes",
		no_notes_entered:
			router.locale === "ar-AE"
				? "لم يتم إدخال ملاحظات"
				: router.locale === "kn-IN"
					? "ಯಾವುದೇ ಟಿಪ್ಪಣಿಗಳು ನಮೂದಿಸಲಾಗಿಲ್ಲ"
					: "No notes entered",
		this_is_how_your_broadcast_will_look_like:
			router.locale === "ar-AE"
				? "هذا هو شكل البث الخاص بك"
				: router.locale === "kn-IN"
					? "ಇದು ನಿಮ್ಮ ಪ್ರಸಾರ ಹೇಗೆ ಕಾಣಿಸುತ್ತದೆ"
					: "This is how your broadcast will look like",
	};

	return (
		<Container>
			<HeaderContainer>
				<Title variant="h5">{localLang.preview_your_broadcast_details}</Title>

				<SubTitle variant="caption">{localLang.this_is_how_your_broadcast_will_look_like}</SubTitle>

				<PropertyCardBroadcastPreviewFetcher
					PropertyId={propertyId}
					dispatch={dispatch}
					router={router}
					source="broadcast_preview"
					broadcast_commission={broadcast?.commission_offered || 0}
					broadcast_notes={broadcast?.notes || ""}
				/>
			</HeaderContainer>

			<Section>
				<ValueSection>
					<ValueContainer>
						<Label>{localLang.broadcasting_to}</Label>{" "}
					</ValueContainer>
					<Value variant="body1">{(broadcast?.no_of_agents || 0) + " " + localLang.agents}</Value>
				</ValueSection>

				<ValueSection>
					<ValueContainer>
						<Label>{localLang.brokerage_offered}</Label>
						{!loading ? (
							<EditOutlined
								onClick={() => setPage(2)}
								sx={{
									cursor: "pointer",
									color: "text.secondary",
								}}
							/>
						) : null}
					</ValueContainer>
					<Value variant="body1">{broadcast?.commission_offered || 0}%</Value>
				</ValueSection>

				<ValueSection>
					<ValueContainer>
						<Label>{localLang.additional_note}</Label>
						{!loading ? (
							<EditOutlined
								onClick={() => setPage(3)}
								sx={{
									cursor: "pointer",
									color: "text.secondary",
								}}
							/>
						) : null}
					</ValueContainer>
					<Value
						variant="subtitle1"
						sx={{
							fontWeight: 400,
						}}
					>
						{broadcast?.notes || localLang.no_notes_entered}
					</Value>
				</ValueSection>
			</Section>
		</Container>
	);
}
